import React, { useState } from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
// Removed @szhsin/react-accordion import as we're using custom implementation
import { bool, arrayOf, number, shape } from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';

import { richText } from '../../util/richText';
import { withViewport } from '../../util/uiHelpers';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  propTypes,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_MULTI_ENUM,
  REVIEW_TYPE_OF_CUSTOMER,
  REVIEW_TYPE_OF_PROVIDER,
} from '../../util/types';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import {
  H2,
  H3,
  H4,
  Page,
  Avatar,
  Heading,
  Reviews,
  NamedLink,
  AvatarLarge,
  IconSpinner,
  ListingCard,
  AccordionItem,
  NamedRedirect,
  ListingCardNew,
  LayoutSingleColumn,
  ButtonTabNavHorizontal,
} from '../../components';

import NotFoundPage from '../NotFoundPage/NotFoundPage';
import SectionMapMaybe from '../ListingPage/SectionMapMaybe';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ProfilePageCustom.module.css';
import SectionTextMaybe from './SectionTextMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionReviewsForProvider from '../ListingPage/SectionReviewsForProvider';

import {
  PROFILE_DEFAULT,
  PROFILE_REVIEWS,
  PROFILE_LISTINGS,
  PROFILE_CERTIFICATES,
} from '../../marketplace-custom-config';
import { ReactComponent as EmailSVG } from '../../assets/svg/email.svg';
import { ReactComponent as GlobeSVG } from '../../assets/svg/globe.svg';
import { ReactComponent as PhoneSVG } from '../../assets/svg/phone.svg';
import { ReactComponent as ShareSVG } from '../../assets/svg/share.svg';
import { ReactComponent as CommentSVG } from '../../assets/svg/comment.svg';
import { ReactComponent as PDFIconSVG } from '../../assets/svg/pdfIcon.svg';
import { ReactComponent as LocationSVG } from '../../assets/svg/location.svg';
import { ReactComponent as WhatsAppSVG } from '../../assets/svg/whats-app.svg';
import { ReactComponent as ContactPersonSVG } from '../../assets/svg/contact-person.svg';
import { ReactComponent as NoCertificatesSVG } from '../../assets/svg/no-certificates.svg';
import { ReactComponent as InstagramSVG } from '../../assets/svg/instagram.svg';
import { ReactComponent as LinkedInSVG } from '../../assets/svg/linkedIn.svg';
import { ReactComponent as XSVG } from '../../assets/svg/x.svg';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;

  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {isCurrentUser ? (
        <React.Fragment>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </React.Fragment>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;

  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError } = props;
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;
  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <React.Fragment>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe {...fieldProps} />
        ) : null;
      })}
    </React.Fragment>
  );
};

export const MainContent = props => {
  const {
    bio,
    listings,
    viewport,
    currentUser,
    userShowError,
    queryListingsError,
    intl,
    reviews,
    metadata,
    publicData,
    displayName,
    userFieldConfig,
    queryReviewsError,
  } = props;
  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordClass: css.longWord,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  return (
    <section>
      {/* <H3 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H3> */}

      {/* {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null} */}

      {/* <CustomUserFields
        publicData={publicData}
        metadata={metadata}
        userFieldConfig={userFieldConfig}
        intl={intl}
      /> */}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          {/* <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4> */}
          {/* <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul> */}
          <ul className={css.listingCardsCustom}>
            {listings.map(l => (
              <ListingCardNew
                listing={l}
                key={l.id.uuid}
                author={l.author}
                showAuthorInfo={true}
                currentUser={currentUser}
                className={css.listingCardCustom}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {/* {isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      )} */}
    </section>
  );
};

const VendorCertificates = props => {
  const { additionalImageOrVedios } = props;

  const awsPreviewFile = file => {
    if (file?.location?.includes('/document/') || file?.location?.includes('/images/')) {
      return (
        <div className={css.fileBox}>
          <div className={css.fileInformation}>
            <PDFIconSVG />
            <div>
              <p
                className={css.fileName}
                onClick={() => window.open(file.location, '_blank')}
                title={file.key?.split('/')[1]?.replace(/^\d+-/, '')}
              >
                {file.key?.split('/')[1]?.replace(/^\d+-/, '')}
              </p>
              <p className={css.fileSize}>{(+file.size / 1000).toFixed()} KB</p>
            </div>
          </div>
          <div className={css.fileClose}></div>
        </div>
      );
    } else if (file?.location?.includes('/videos/')) {
      // Ignored because dealing with documents
      return null;
    } else {
      return null;
    }
  };

  return (
    <section className={css.mediaContainer}>
      {additionalImageOrVedios?.map((file, i) => (
        <div key={i} className={css.previewImageWrapper}>
          {awsPreviewFile(file)}
        </div>
      ))}
    </section>
  );
};

export const ProfilePageCustomComponent = props => {
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const searchParams = new URLSearchParams(location.search);

  const {
    user,
    intl,
    params,
    currentUser,
    currentAuthor,
    userShowError,
    scrollingDisabled,
    reviewsLoading,
    reviewsData,
    reviewsError,
    sendReplyInProgress,
    sendReplySuccess,
    sendReplyError,
    onSendReply,
    ...rest
  } = props;

  const profileUser = ensureUser(user);
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;

  if (isCurrentUser) {
    return <NamedRedirect name="ProfileSettingsPage" />;
  }

  const {
    displayName,
    firstName,
    lastName,
    bio,
    publicData,
    protectedData,
    locationAddress = protectedData?.locationAddress,
    services = publicData?.services,
    businesses = publicData?.businesses,
    phoneNumber = publicData?.phoneNumber,
    alternativePhoneNumber = publicData?.alternativePhoneNumber,
    companyName = publicData?.companyName,
    companyEmail = publicData?.companyEmail,
    websiteLink = publicData?.websiteLink,
    socialMediaLink = publicData?.socialMediaLink,
    linkedInLink = publicData?.linkedInLink || '',
    xHandle = publicData?.xHandle || '',
    contactPersonEmail = publicData?.contactPersonEmail,
    contactPersonPhoneNumber = publicData?.contactPersonPhoneNumber,
    additionalImageOrVedios = publicData?.additionalImageOrVedios,
  } = currentAuthor?.attributes.profile || {};
  const { customUserType } = publicData || {};
  const isLoginAsVendor = customUserType === 'vendor';
  const origin = locationAddress?.selectedPlace?.origin;
  const address = locationAddress?.selectedPlace?.address;
  const isPremiumVendor = user?.attributes?.profile?.publicData?.isPremiumVendor || false;

  const updateSearchParams = (key, value) => {
    let params = new URLSearchParams(searchParams);
    params.set(key, value);
    history.push({ search: params.toString() });
  };

  const tabFromURL = searchParams.get('tab');
  const tab = [PROFILE_DEFAULT, PROFILE_CERTIFICATES, PROFILE_REVIEWS, PROFILE_LISTINGS].includes(
    tabFromURL
  )
    ? tabFromURL
    : PROFILE_DEFAULT;

  // Design for selected tab
  const defaultTabClass = classNames({
    [css.defaultTab]: tab === PROFILE_DEFAULT,
  });
  const reviewsTabClass = classNames({
    [css.reviewsTab]: tab === PROFILE_REVIEWS,
  });
  const listingsTabClass = classNames({
    [css.listingsTab]: tab === PROFILE_LISTINGS,
  });
  const certificatesTabClass = classNames({
    [css.certificatesTab]: tab === PROFILE_CERTIFICATES,
  });

  // Design for selected tab in mobile device
  const defaultTabClassForMobile = classNames({
    [css.defaultTabForMobile]: tab === PROFILE_DEFAULT,
  });
  const reviewsTabClassForMobile = classNames({
    [css.reviewsTabForMobile]: tab === PROFILE_REVIEWS,
  });
  const listingsTabClassForMobile = classNames({
    [css.listingsTabForMobile]: tab === PROFILE_LISTINGS,
  });
  const certificatesTabClassForMobile = classNames({
    [css.certificatesTabForMobile]: tab === PROFILE_CERTIFICATES,
  });

  const updatedServices = [];
  const updatedBusinesses = businesses ?? [
    {
      category: null,
      subCategory: null,
      subSubCategory: null,
    },
  ];

  if (services?.length) {
    services.map(service => {
      updatedServices.push(service);
    });
  }

  const categories = [];
  const subCategories = [];
  const subSubCategories = [];
  const categoryList = config?.categoryConfiguration?.categories;
  const result = {
    subCategories: [],
    subSubCategories: [],
    categories: categoryList.map(category => ({ id: category.id, name: category.name })),
  };

  categoryList?.forEach(category => {
    category.subcategories.forEach(sub => {
      result.subCategories.push({ id: sub.id, name: sub.name });

      sub.subcategories.forEach(subSub => {
        result.subSubCategories.push({ id: subSub.id, name: subSub.name });
      });
    });
  });

  updatedBusinesses?.forEach(updatedBusiness => {
    result.categories?.forEach(category => {
      if (category?.id === updatedBusiness?.category) {
        categories.push({ label: category?.name, value: category?.id });
      }
    });
  });

  updatedBusinesses?.forEach(updatedBusiness => {
    if (!updatedBusiness?.subCategory) {
      subCategories.push({});
    } else {
      result.subCategories?.forEach(subCategory => {
        if (subCategory?.id === updatedBusiness?.subCategory) {
          subCategories.push({ label: subCategory?.name, value: subCategory?.id });
        }
      });
    }
  });

  updatedBusinesses?.forEach(updatedBusiness => {
    if (!updatedBusiness?.subSubCategory) {
      subSubCategories.push({});
    } else {
      result.subSubCategories?.forEach(subSubCategory => {
        if (subSubCategory?.id === updatedBusiness?.subSubCategory) {
          subSubCategories.push({ label: subSubCategory?.name, value: subSubCategory?.id });
        }
      });
    }
  });

  const callNumber = phoneNumber?.number;
  const callDialCode = phoneNumber?.dialCode;
  const vendorCallNumber = `${callDialCode}${callNumber}`;
  const whatsAppNumber = alternativePhoneNumber?.number;
  const whatsAppDialCode = alternativePhoneNumber?.dialCode;
  const vendorWhatsAppNumber = `${whatsAppDialCode}${whatsAppNumber}`;
  const cpCallNumber = contactPersonPhoneNumber?.number;
  const cpDialCode = contactPersonPhoneNumber?.dialCode;
  const contactPersonCallNumber = `${cpDialCode}${cpCallNumber}`;

  const { userFields } = config.user;
  const { metadata } = profileUser?.attributes?.profile || {};
  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const rawMsg = intl.formatMessage(
    { id: 'ProfileSettingsPage.whatsAppMsg' },
    { marketplaceName: config.marketplaceName }
  );
  const msg = encodeURIComponent(rawMsg);

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        {profileUser?.id?.uuid && (
          <div className={css.contentCustom}>
            <aside className={css.tabsWrapperForMobile}>
              <nav className={css.tabsForMobile}>
                <div className={css.tabForMobile}>
                  <span
                    onClick={() => updateSearchParams('tab', PROFILE_DEFAULT)}
                    className={classNames(css.tabLabelForMobile, defaultTabClassForMobile)}
                  >
                    Company Information
                  </span>
                </div>
                <div className={css.tabForMobile}>
                  <span
                    onClick={() => updateSearchParams('tab', PROFILE_CERTIFICATES)}
                    className={classNames(css.tabLabelForMobile, certificatesTabClassForMobile)}
                  >
                    Certificates
                  </span>
                </div>
                <div className={css.tabForMobile}>
                  <span
                    onClick={() => updateSearchParams('tab', PROFILE_REVIEWS)}
                    className={classNames(
                      css.tabForMobile,
                      css.tabLabelForMobile,
                      reviewsTabClassForMobile
                    )}
                  >
                    Reviews & Ratings
                  </span>
                </div>
                <div className={css.tabForMobile}>
                  <span
                    onClick={() => updateSearchParams('tab', PROFILE_LISTINGS)}
                    className={classNames(
                      css.tabForMobile,
                      css.tabLabelForMobile,
                      listingsTabClassForMobile
                    )}
                  >
                    Listings
                  </span>
                </div>
              </nav>
            </aside>

            <ul className={css.tabs}>
              <li
                className={classNames(css.tabLabel, defaultTabClass)}
                onClick={() => updateSearchParams('tab', PROFILE_DEFAULT)}
              >
                Company Information
              </li>
              <li
                className={classNames(css.tabLabel, certificatesTabClass)}
                onClick={() => updateSearchParams('tab', PROFILE_CERTIFICATES)}
              >
                Certificates
              </li>
              <li
                className={classNames(css.tabLabel, reviewsTabClass)}
                onClick={() => updateSearchParams('tab', PROFILE_REVIEWS)}
              >
                Reviews & Ratings
              </li>
              <li
                className={classNames(css.tabLabel, listingsTabClass)}
                onClick={() => updateSearchParams('tab', PROFILE_LISTINGS)}
              >
                Listings
              </li>
            </ul>

            <div className={css.ruler} />

            <div className={css.mainSectionContainer}>
              {tab === PROFILE_DEFAULT ? (
                <div className={classNames(css.defaultSection)}>
                  <div className={css.profilePictureContainer}>
                    <Avatar className={css.avatar} user={profileUser} disableProfileLink />
                  </div>

                  <div className={classNames(css.profileInformationSection)}>
                    <h4 className={classNames({ [css.requiredClass]: !companyName })}>
                      {companyName ? companyName : 'Unknown Company'}
                    </h4>

                    {/* Chips */}
                    {isLoginAsVendor && updatedServices?.length ? (
                      <div className={css.serviceChipContainer}>
                        {updatedServices?.map(service => (
                          <div key={service?.label} className={css.serviceChip}>
                            {service?.label}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div className={css.contactWithIcon}>
                      {/* Email */}
                      <div>
                        <EmailSVG />
                        <span className={classNames({ [css.requiredClass]: !companyEmail })}>
                          {companyEmail ? (
                            <a href={`mailto:${companyEmail}`} className={css.anchorTag}>
                              {companyEmail}
                            </a>
                          ) : (
                            'Email not available'
                          )}
                        </span>
                      </div>

                      {/* Phone */}
                      {callDialCode && callNumber && (
                        <div className={css.call}>
                          <PhoneSVG />
                          <a className={css.anchorTag} href={`tel:${vendorCallNumber}`}>
                            {vendorCallNumber}
                          </a>
                        </div>
                      )}

                      {/* WhatsApp */}
                      {whatsAppDialCode && whatsAppNumber && (
                        <div>
                          <WhatsAppSVG />
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://api.whatsapp.com/send?phone=${vendorWhatsAppNumber}&text=${msg}`}
                            className={css.anchorTag}
                          >
                            {vendorWhatsAppNumber}
                          </a>
                        </div>
                      )}

                      {/* Website Link */}
                      {isLoginAsVendor && websiteLink ? (
                        <div>
                          <GlobeSVG />
                          <a
                            className={css.anchorTag}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={websiteLink}
                          >
                            {websiteLink}
                          </a>
                        </div>
                      ) : null}

                      {/* Social Media Link */}
                      {isLoginAsVendor && socialMediaLink && isPremiumVendor ? (
                        <div>
                          {/* <ShareSVG /> */}
                          <InstagramSVG className={css.contactIcon} />
                          <a
                            className={css.anchorTag}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={socialMediaLink}
                          >
                            {socialMediaLink}
                          </a>
                        </div>
                      ) : null}

                      {isLoginAsVendor && linkedInLink && isPremiumVendor ? (
                        <div>
                          <LinkedInSVG className={css.contactIcon} />
                          <a
                            className={css.anchorTag}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={linkedInLink}
                          >
                            {linkedInLink}
                          </a>
                        </div>
                      ) : null}

                      {isLoginAsVendor && xHandle && isPremiumVendor ? (
                        <div>
                          <XSVG className={css.contactIcon} />
                          <a
                            className={css.anchorTag}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={xHandle}
                          >
                            {xHandle}
                          </a>
                        </div>
                      ) : null}

                      {address && (
                        <div
                          className={classNames(css.address, css.anchorTag)}
                          onClick={() => {
                            if (window) {
                              window.open(`https://www.google.com/maps?q=${address}`, '_blank');
                            }
                          }}
                        >
                          <LocationSVG />
                          <span>{address}</span>
                        </div>
                      )}
                    </div>

                    {origin && (
                      <div className={css.mapContainer}>
                        <SectionMapMaybe
                          listingId={null}
                          geolocation={origin}
                          publicData={publicData}
                          isLocationTitle={false}
                          mapsConfig={config.maps}
                        />
                      </div>
                    )}

                    {bio && <p>{bio}</p>}

                    {/* Accordion */}
                    {categories.length > 0 ? (
                      <div className={css.accordion}>
                        <article>
                          {categories.map((_, index) => (
                            <AccordionItem
                              key={index}
                              initialEntered={index === -1}
                              header={categories?.[index]?.label}
                            >
                              {/* categories for listing */}
                              <section className={css.categoryDetails}>
                                {subCategories?.[index]?.label ? (
                                  <div>
                                    <span>Sub-Categories:</span>
                                    <span>{subCategories?.[index]?.label}</span>
                                  </div>
                                ) : null}
                                {subSubCategories?.[index]?.label ? (
                                  <div>
                                    <span>Sub-Sub-Categories:</span>
                                    <span>{subSubCategories?.[index]?.label}</span>
                                  </div>
                                ) : null}
                              </section>
                            </AccordionItem>
                          ))}
                        </article>
                      </div>
                    ) : null}

                    {isLoginAsVendor && (
                      <div className={css.contactPerson}>
                        <h5>Contact Person</h5>

                        <div className={css.contactPersonName}>
                          <ContactPersonSVG />
                          <span>
                            {firstName} {lastName}
                          </span>
                        </div>

                        {/* Email*/}
                        {contactPersonEmail && (
                          <div className={css.contactPersonEmail}>
                            <EmailSVG />
                            <a href={`mailto:${contactPersonEmail}`} className={css.anchorTag}>
                              {contactPersonEmail}
                            </a>
                          </div>
                        )}

                        {/* Phone */}
                        {cpDialCode && cpCallNumber && (
                          <div className={css.contactPersonPhoneNumber}>
                            <PhoneSVG />
                            <a className={css.anchorTag} href={`tel:${contactPersonCallNumber}`}>
                              {contactPersonCallNumber}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : tab === PROFILE_CERTIFICATES ? (
                additionalImageOrVedios?.length > 0 ? (
                  <article className={css.certificatesRoot}>
                    <VendorCertificates additionalImageOrVedios={additionalImageOrVedios} />
                  </article>
                ) : (
                  <div className={classNames(css.certificatesEditSection)}>
                    <NoCertificatesSVG />
                    <div>
                      <h3>No Certificates</h3>
                      <h3>Available yet to show</h3>
                    </div>
                  </div>
                )
              ) : tab === PROFILE_REVIEWS ? (
                <div className={classNames(css.reviewSection)}>
                  {reviewsLoading ? (
                    <IconSpinner />
                  ) : reviewsError ? (
                    <span>Unable to load reviews</span>
                  ) : reviewsData?._id ? (
                    <SectionReviewsForProvider
                      history={history}
                      author={currentUser}
                      onSendReply={onSendReply}
                      reviewsData={reviewsData}
                      routes={routeConfiguration}
                      sendReplyError={sendReplyError}
                      fetchReviewsError={reviewsError}
                      sendReplySuccess={sendReplySuccess}
                      sendReplyInProgress={sendReplyInProgress}
                      authorDisplayName={currentUser?.attributes?.profile?.displayName}
                    />
                  ) : (
                    <div className={classNames(css.preEditSection, css.preReviewSection)}>
                      <CommentSVG />
                      <div>
                        <h3>No Rating</h3>
                        <h3>Available yet to show</h3>
                      </div>
                    </div>
                  )}
                </div>
              ) : tab === PROFILE_LISTINGS ? (
                <div className={classNames(css.mainContentSection)}>
                  <MainContent
                    bio={bio}
                    intl={intl}
                    metadata={metadata}
                    publicData={publicData}
                    currentUser={currentUser}
                    displayName={displayName}
                    userFieldConfig={userFields}
                    userShowError={userShowError}
                    {...rest}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageCustomComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageCustomComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { sendReplyInProgress, sendReplySuccess, sendReplyError } = state.ListingPage;
  const {
    userId,
    currentAuthor,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    reviewsLoading,
    reviewsData,
    reviewsError,
  } = state.ProfilePage;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    currentAuthor,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    reviewsLoading,
    reviewsData,
    reviewsError,
    sendReplyInProgress,
    sendReplySuccess,
    sendReplyError,
  };
};

const ProfilePageCustom = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageCustomComponent);

export default ProfilePageCustom;
